import StatefulSettings from "../controllers/StatefulSettings";
import {
  IEventItem,
  ISchedulerTimerInfo,
  ICustomSchedule,
  TaskItem,
  IScheduleOptions,
  TaskBasketItem,
  PenaltyBasketItem,
  RollState,
  EventBasketItem,
  PermissionBasketItem,
  AuditMessage,
} from "../ts/Definitions";
import {
  EventType,
  ItemTypes,
  AuditActionTypes,
  TraceLevel,
  ReportStyle,
} from "../ts/enums";
import { scheduleEvent, scheduleTask, convertToEventDayFromDate, formatValidDate } from "../utilities/dateUtils";
import { isAvailableItem, getDecimalRandom, getRandom } from "../utilities/utils";
import { MessageType, sendMessageToMember } from "../controllers/messaging";
import { OUTFIT_EVENT_NAME } from "../components/wizardUtils";

const MORNING_REPORT_ID = "MORNING_REPORT_ID";
const DAILY_REPORT_ID = "DAILY_REPORT_ID";

/**
 * EventScheduleManager - Handles all event scheduling operations including:
 * - Schedule checking and firing
 * - Auto-scheduled events setup
 * - Custom schedule events setup
 * - Task scheduling
 * - Schedule item completion
 */
export class EventScheduleManager {
  constructor(
    private settings: StatefulSettings,
    private onEventComplete: (
      item: IEventItem,
      overrideDecision?: boolean,
      reason?: string,
      ignoreMessage?: boolean,
      bypassAdditionChecks?: boolean
    ) => EventBasketItem | undefined,
    private addPermissionToBasket: (itemKey: string) => PermissionBasketItem | undefined,
    private handleMissedScheduleItem: (
      reportTime: Date,
      item: { name: string; missEventPenalties?: string[]; missEventPillories?: string[] },
      basketItem: PermissionBasketItem | EventBasketItem | undefined
    ) => void,
    private handleDailyReportRequest: () => void,
    private handleMorningReportRequest: () => void,
    private onPermissionRequested: (
      itemKey: string,
      overrideDecision?: boolean,
      additionalInfo?: any
    ) => PermissionBasketItem | undefined,
    private onStartOfDay: (rollState: RollState) => Promise<void>,
    private handleAutoProofRequest: (items: (TaskBasketItem | PenaltyBasketItem)[] | undefined) => void,
    private getCurrentState: () => string,
    private handleScheduledAttendance: (scheduleItem: ISchedulerTimerInfo) => void
  ) { }

  private parseTimeForToday(value?: string): Date | undefined {
    if (!value) return undefined;
    const [hourStr, minuteStr] = value.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    return date;
  }

  private parseClockToMinutes(value?: string): number | undefined {
    if (!value) return undefined;
    const [hourStr, minuteStr] = value.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;
    return (hour * 60) + minute;
  }

  private formatMinutesAsClock(totalMinutes: number): string {
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  }

  private getTaskNameFromKeys(taskKeys?: string[]): string | undefined {
    const firstKey = (taskKeys ?? []).map((key) => (key ?? "").trim()).find(Boolean);
    if (!firstKey) return undefined;
    const task = this.settings.getTask(firstKey);
    return task?.name;
  }

  private isReadinessExcludedEvent(event: { key?: string; name?: string; eventType?: EventType }): boolean {
    if (event.eventType === EventType.startOfDay) return true;

    const key = (event.key ?? "").trim();
    const firstLineName = ((event.name ?? "").split("\n")[0] ?? "").trim();
    if (firstLineName === OUTFIT_EVENT_NAME) return true;
    if (key === `event-${OUTFIT_EVENT_NAME}`) return true;
    return false;
  }

  private isSameCalendarDay(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  }

  private hasRemainingEventForDay(day: Date): boolean {
    return this.settings.getScheduleInfo().some((item) => {
      if (item.itemType !== ItemTypes.event) return false;
      if (item.eventType === EventType.startOfDay) return false;
      if (item.key === DAILY_REPORT_ID || item.key === MORNING_REPORT_ID) return false;
      const start = item.startTime ? new Date(item.startTime) : undefined;
      if (!start || Number.isNaN(start.getTime())) return false;
      return this.isSameCalendarDay(start, day);
    });
  }

  private getMorningReadinessInfo(events: Array<{ key?: string; name?: string; eventType?: EventType; minTime?: string; taskKeys?: string[] }>): { time: string; firstTaskName?: string } | undefined {
    let earliestEvent: { minutes: number; taskName?: string } | undefined;
    events.forEach((event) => {
      if (this.isReadinessExcludedEvent(event)) return;
      const minutes = this.parseClockToMinutes(event.minTime);
      if (minutes === undefined) return;
      // Morning-only reminder: noon and later is not included.
      if (minutes >= 12 * 60) return;
      if (!earliestEvent || minutes < earliestEvent.minutes) {
        earliestEvent = {
          minutes,
          taskName: this.getTaskNameFromKeys(event.taskKeys),
        };
      }
    });
    if (!earliestEvent) return undefined;
    return {
      time: this.formatMinutesAsClock(earliestEvent.minutes),
      firstTaskName: earliestEvent.taskName,
    };
  }

  private getTomorrowEventDay(): number {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return convertToEventDayFromDate(tomorrow.getDay());
  }

  private async getTomorrowMorningReadinessInfo(): Promise<{ time: string; firstTaskName?: string } | undefined> {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDay = this.getTomorrowEventDay();
    const customSchedule = this.settings.getScheduledCustomSchedule(tomorrowDay, tomorrow);
    if (customSchedule?.events?.length) {
      const info = this.getMorningReadinessInfo(customSchedule.events);
      if (info) return info;
    }

    if (customSchedule?.key) {
      const detail = await this.settings.loadCustomScheduleDetail(customSchedule.key);
      if (detail?.events?.length) {
        const info = this.getMorningReadinessInfo(detail.events);
        if (info) return info;
      }
    }

    const sessionSchedule = this.settings.getDaySessionSchedule(tomorrowDay, false, false, false);
    return this.getMorningReadinessInfo(sessionSchedule.events ?? []);
  }

  private notifyTomorrowMorningReadiness(): void {
    const control = this.settings.getControl();
    if (!control) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (!this.settings.canPerformSomeTasksForDate(tomorrow)) return;

    void this.getTomorrowMorningReadinessInfo().then((readinessInfo) => {
      if (!readinessInfo?.time) return;
      const owner = control.getOwner?.() ?? control;
      const taskLine = readinessInfo.firstTaskName
        ? `\nFirst task: ${readinessInfo.firstTaskName}.`
        : "";
      sendMessageToMember(
        MessageType.MemberMessage,
        this.settings,
        `🌅 Tomorrow readiness reminder\n\nTomorrow's first scheduled event is at ${readinessInfo.time}.${taskLine}\nPlease be awake and ready before this time so you can complete tasks on schedule.`,
        `🌅 Be ready by ${readinessInfo.time} tomorrow`,
        [control],
        owner,
        true
      );
    });
  }

  private getCustomAttendanceWindow(customSchedule: ICustomSchedule): { start: Date; end: Date } | undefined {
    const times = (customSchedule.events ?? [])
      .flatMap((event) => [
        this.parseTimeForToday(event.minTime),
        this.parseTimeForToday(event.maxTime || event.minTime),
      ])
      .filter((time): time is Date => Boolean(time))
      .sort((a, b) => a.getTime() - b.getTime());
    if (times.length === 0) return undefined;
    return { start: times[0], end: times[times.length - 1] };
  }

  private createCustomAttendanceScheduleItems(customSchedule: ICustomSchedule): ISchedulerTimerInfo[] {
    if (!customSchedule.attendanceEnabled || !this.settings.canPerformSomeTasksForToday()) return [];
    const count = customSchedule.attendanceCount ?? 0;
    if (count <= 0) return [];

    const window = this.getCustomAttendanceWindow(customSchedule);
    if (!window) return [];

    const rangeMinutes = Math.max(0, Math.floor((window.end.getTime() - window.start.getTime()) / 60000));
    const maxSlots = Math.max(1, rangeMinutes + 1);
    const totalCount = Math.min(count, maxSlots);
    const minuteOffsets = new Set<number>();
    while (minuteOffsets.size < totalCount) {
      minuteOffsets.add(rangeMinutes <= 0 ? 0 : getRandom(0, rangeMinutes));
    }

    return [...minuteOffsets].sort((a, b) => a - b).map((offset, index) => {
      const startTime = new Date(window.start.getTime() + offset * 60000);
      const scheduledItem = {
        key: `custom-attendance-${customSchedule.key}-${index}`,
        name: `Attendance Request ${index + 1}`,
        description: `Automated attendance request from custom schedule ${customSchedule.name}`,
        img: "",
        imgId: "",
        minTime: "",
        maxTime: "",
        itemType: ItemTypes.event,
        eventType: EventType.standard,
        disableAdd: false,
        startTime,
        duration: 0,
        scheduleOptions: {
          isCustomSchedule: true,
          customAttendance: {
            holdMinMinutes: Math.max(1, customSchedule.attendanceMinMinutes ?? 10),
            holdMaxMinutes: Math.max(
              Math.max(1, customSchedule.attendanceMinMinutes ?? 10),
              customSchedule.attendanceMaxMinutes ?? 20
            ),
            responseTimeoutSec: Math.max(15, customSchedule.attendanceResponseTimeoutSec ?? 60),
            penaltyKeys: [...(customSchedule.attendancePenaltyKeys ?? [])],
            positionKeys: [...(customSchedule.attendancePositionKeys ?? [])],
          },
        },
        isPaused: true,
        isComplete: false,
        scheduleReason: `Automated attendance request for custom schedule ${customSchedule.name}`,
      } as unknown as ISchedulerTimerInfo;
      this.settings.updateSchedulerState(scheduledItem);
      return scheduledItem;
    });
  }

  /**
   * Checks if a scheduled item can be fired
   */
  public canFireEvent(scheduleItem: ISchedulerTimerInfo): boolean {
    const now = new Date();
    const reportTime =
      scheduleItem.startTime && new Date(scheduleItem.startTime);
    return scheduleItem && reportTime && reportTime <= now ? true : false;
  }

  /**
   * Removes an item from the schedule
   */
  public removeItemFromSchedule(scheduleItemKey: string): void {
    const localScheduler = this.settings.getScheduleInfo();
    const idx = localScheduler.findIndex(
      (item) => scheduleItemKey === item.key
    );
    if (idx > -1) {
      localScheduler.splice(idx, 1);
      this.settings.deleteSchedulerState(scheduleItemKey);
    }
  }

  /**
   * On timer tick, check and fire completed scheduler entries that are in the past
   * @returns true if it fired off a new day initialisation
   */
  public async checkScheduleForComplete(): Promise<boolean> {
    if (this.getCurrentState() === "rollingDay") return false;
    const iterator = [...this.settings.getScheduleInfo()];

    const sodEvent = iterator.find((i) => i.eventType === EventType.startOfDay);
    if (sodEvent && this.canFireEvent(sodEvent)) {
      // delete the current item as we are handling it
      this.removeItemFromSchedule(sodEvent.key);
      await this.onStartOfDay(sodEvent.scheduleOptions?.state as RollState);
      return true; // this will wipe all other events, so no point waiting for them
    }

    for (let i = 0; i < iterator.length; i += 1) {
      const scheduleItem = iterator[i];
      this.onScheduleItemComplete(scheduleItem);
    }
    return false;
  }

  /**
   * Handles completion of a scheduled item
   */
  public onScheduleItemComplete(scheduleItem: ISchedulerTimerInfo): void {
    const reportTime =
      scheduleItem.startTime && new Date(scheduleItem.startTime);
    if (this.canFireEvent(scheduleItem) && reportTime) {
      const generalSettings = this.settings.getGeneralSettings();
      const isDailyReportRequest =
        scheduleItem.scheduleOptions?.dailyReport === true ||
        scheduleItem.key === DAILY_REPORT_ID;
      const isMorningReportRequest =
        scheduleItem.scheduleOptions?.morningReport === true ||
        scheduleItem.key === MORNING_REPORT_ID;
      const isCompletingRegularEvent =
        scheduleItem.itemType === ItemTypes.event
        && !isDailyReportRequest
        && !isMorningReportRequest
        && scheduleItem.eventType !== EventType.startOfDay;
      // delete the current item as we are handling it
      this.removeItemFromSchedule(scheduleItem.key);

      if (isDailyReportRequest && generalSettings.dailyReporting) {
        this.handleDailyReportRequest();
      } else if (
        isMorningReportRequest &&
        generalSettings.reportStyle !== ReportStyle.once &&
        generalSettings.dailyReporting
      ) {
        this.handleMorningReportRequest();
      } else if (scheduleItem.scheduleOptions?.customAttendance) {
        this.handleScheduledAttendance(scheduleItem);
      } else if (scheduleItem.scheduleOptions?.expiryCallback) {
        if (scheduleItem.itemType === ItemTypes.permission) {
          // we have expired an admin request so let's handle it ourselves
          this.onPermissionRequested(scheduleItem.key);
        }
      } else {
        this.handleScheduledItemByType(scheduleItem, reportTime);
      }

      if (isCompletingRegularEvent && !this.hasRemainingEventForDay(reportTime)) {
        this.notifyTomorrowMorningReadiness();
      }
    }
  }

  /**
   * Handles scheduled item based on its type
   */
  private handleScheduledItemByType(
    scheduleItem: ISchedulerTimerInfo,
    reportTime: Date
  ): void {
    if (scheduleItem.itemType === ItemTypes.event) {
      const event = this.settings.getEvent(scheduleItem.key);
      const eventToComplete = event
        ? ({
          ...event,
          itemOptions: (scheduleItem as unknown as { itemOptions?: Record<string, string> }).itemOptions,
          selectedTaskKeys: (scheduleItem as unknown as { selectedTaskKeys?: string[] }).selectedTaskKeys,
          runTasksToNextEvent: (scheduleItem as unknown as { runTasksToNextEvent?: boolean }).runTasksToNextEvent,
          taskDurationMinutes: (scheduleItem as unknown as { taskDurationMinutes?: number }).taskDurationMinutes,
          awaitTaskComplete: (scheduleItem as unknown as { awaitTaskComplete?: boolean }).awaitTaskComplete,
        } as IEventItem)
        : scheduleItem;
      const result = this.onEventComplete(
        eventToComplete,
        undefined,
        undefined,
        undefined,
        scheduleItem.scheduleOptions?.isCustomSchedule
      );
      this.handleMissedScheduleItem(
        reportTime,
        event ?? scheduleItem,
        result
      );
    } else if (scheduleItem.itemType === ItemTypes.permission) {
      const result = this.addPermissionToBasket(scheduleItem.key);
      const permission = this.settings.getPermission(scheduleItem.key);
      if (permission)
        this.handleMissedScheduleItem(reportTime, permission, result);
    } else if (scheduleItem.itemType === ItemTypes.task) {
      const copyOfTask = this.settings.getTask(scheduleItem.key);
      if (copyOfTask) {
        // we get here through two branches, enforced have 0 credit, allocated over day have the default credits
        copyOfTask.credit = scheduleItem.duration ?? 0;
        this.settings.addTaskToSchedule(
          copyOfTask,
          scheduleItem.scheduleReason,
          undefined
        );
      }
    } else if (scheduleItem.itemType === ItemTypes.penalty) {
      const copyOfPenalty = this.settings.getPenalty(scheduleItem.key);
      if (copyOfPenalty) {
        this.settings.addPenaltyToSchedule(
          copyOfPenalty,
          scheduleItem.scheduleReason,
          undefined,
          undefined
        );
      }
    }
  }

  /**
   * Schedules a task to be added to basket
   */
  public scheduleAddTaskToBasket(
    item: TaskItem,
    scheduleReason: string
  ): void {
    const creditSettings = this.settings.getTaskSettings();
    if (
      TaskItem.CanTaskBeScheduledInDay(item) &&
      creditSettings.scheduleDailyTasks
    ) {
      const scheduleOptions: IScheduleOptions = {};
      if (creditSettings.maxScheduleTime)
        scheduleOptions.maxTime = creditSettings.maxScheduleTime;
      scheduleTask(
        this.settings,
        item,
        new Date(),
        scheduleReason,
        scheduleOptions
      );
    } else {
      const items = this.settings.addTaskToSchedule(item, scheduleReason, undefined);
      this.handleAutoProofRequest(items);
    }
  }

  /**
   * Sets up auto-scheduled events for the day
   */
  public setupAutoScheduledEvents(): void {
    const eventSettings = this.settings.getEventSettings();
    if (eventSettings.disableEvents) return; // don't set anything up if turned off
    const creditSchedule = this.settings.getTaskSchedule();
    const events = this.settings.getEvents((i) => isAvailableItem(i));
    const scheduler = this.settings.getScheduleInfo();
    const autoScheduledEvents = events.filter(
      (event) =>
        !creditSchedule[event.key] && // not completed already today
        !scheduler.find((sItem) => sItem.key === event.key)
    ); // not already scheduled

    for (let i = 0; i < autoScheduledEvents.length; i++) {
      const event = autoScheduledEvents[i];
      if (
        event.eventType === EventType.startOfDay ||
        !this.settings.itemAvailableForDay(
          event,
          convertToEventDayFromDate(new Date().getDay()),
          false,
          false
        )
      )
        continue; // sod is handled by roll when date delta is found
      if (!this.settings.scheduleItemAvailable(event)) continue;

      const scheduleResult = scheduleEvent(
        this.settings,
        event,
        "This event is scheduled for today based on the diary of the member, their availability and the settings of the events. The events also do not have any categories which are in the limits of the member",
        new Date()
      );

      if (!scheduleResult) {
        this.onEventComplete(
          this.settings.getEvent(event.key) as IEventItem,
          false,
          "Too late to request",
          true
        );
      } else {
        // if this schedule is already due, fire it as we go so that we can update the basket
        // events and their triggers have presendence over enforced credits which fill any gaps left
        this.onScheduleItemComplete(scheduleResult);
      }
    }
  }

  /**
   * Sets up custom schedule events for the day
   */
  public setupCustomScheduleEvents(customSchedule: ICustomSchedule): void {
    if (!this.settings.canPerformSomeTasksForToday()) return; // no point in setting up events if the user is not available
    const events = customSchedule.events ?? [];
    const results: ISchedulerTimerInfo[] = [];

    // Ensure every event has a unique key before scheduling.
    // Duplicate keys cause updateSchedulerState to overwrite the first timer entry with
    // the second, effectively skipping the first event when the schedule fires.
    const usedKeys = new Set<string>();
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      if (!event.key) event.key = event.name + i; // treat as a new keyed event for this custom schedule
      if (usedKeys.has(event.key)) event.key = `${event.key}-${i}`; // deduplicate
      usedKeys.add(event.key);
    }

    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      event.itemType = ItemTypes.event; // force them to be handled as an event if they are not preconfigured properly

      if (event.approvalChance && event.approvalChance < 1) {
        const chanceDecision =
          getDecimalRandom(0, 1) < event.approvalChance;
        if (!chanceDecision) {
          this.onEventComplete(
            event,
            false,
            `Custom Schedule Event ${event.name} skipped today through random chance settings`,
            true,
            true
          );
          continue;
        }
      }

      const scheduleResult = scheduleEvent(
        this.settings,
        event,
        "This event is scheduled for today based on the diary of the member, their availability and the settings of the events. The events also do not have any categories which are in the limits of the member",
        new Date(),
        { isCustomSchedule: true }
      );

      if (!scheduleResult) {
        this.onEventComplete(
          event,
          false,
          "unable to schedule event from custom schedule",
          true,
          true
        );
      } else {
        results.push(scheduleResult);
      }
    }

    results.push(...this.createCustomAttendanceScheduleItems(customSchedule));

    // First set them all up and then fire the ones that are completed
    results.forEach((scheduleResult) =>
      this.onScheduleItemComplete(scheduleResult)
    );

    // Clean up pending item for this custom schedule
    const pendingState = this.settings.getPendingState();
    const pendingItem = pendingState.find(
      (s) =>
        s.customScheduleKey === customSchedule.key &&
        s.date === formatValidDate(new Date(), "dd MMM yyyy")
    );
    if (pendingItem) {
      this.settings.updatePendingItems(pendingItem, true);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.DailyChecks,
          customSchedule.name,
          `Removed Custom Schedule ${customSchedule.name}`,
          TraceLevel.info
        )
      );
    }
  }
}
